import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { myLocalStorage } from "../../StorageHelper";
import { useToast } from "../../../hooks/use-toast";
import {
  clearAdminNotifications,
  fetchAdminNotifications,
  markAdminNotificationsSeen,
  markAllAdminNotificationsRead,
  updateAdminNotificationStatus,
  type AdminNotification,
  type AdminSettableStatus,
  type NotificationStatus,
} from "./Api/adminNotificationApi";

const POLL_INTERVAL_MS = 2000;
const REMOVED = "REMOVED";

type Override = NotificationStatus | typeof REMOVED;

function currentTenantName(): string {
  const latestTenant = myLocalStorage.getItem("latestTenant");
  return latestTenant?.tenantName || (typeof latestTenant === "string" ? latestTenant : "") || "";
}

function applyOverrides(list: AdminNotification[], overrides: Map<string, Override>): AdminNotification[] {
  if (overrides.size === 0) return list;
  return list.reduce<AdminNotification[]>((result, notification) => {
    const override = overrides.get(notification.id);
    if (override === REMOVED) return result;
    result.push(override ? { ...notification, status: override } : notification);
    return result;
  }, []);
}

function signatureOf(list: AdminNotification[]): string {
  return JSON.stringify(
    list.map((notification) => [
      notification.id,
      notification.status,
      notification.title,
      notification.details,
      notification.severity,
      notification.expiresAt,
    ]),
  );
}

export default function useAdminNotifications(open: boolean) {
  const { toast } = useToast();
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [sessionNewIds, setSessionNewIds] = useState<Set<string>>(() => new Set());
  const serverListRef = useRef<AdminNotification[]>([]);
  const overridesRef = useRef<Map<string, Override>>(new Map());
  const signatureRef = useRef("");
  const mutationVersionRef = useRef(0);
  const pollInFlightRef = useRef(false);
  const seenRequestedRef = useRef<Set<string>>(new Set());
  const tenantRef = useRef(currentTenantName());

  const publish = useCallback(() => {
    const next = applyOverrides(serverListRef.current, overridesRef.current);
    const signature = signatureOf(next);
    if (signature !== signatureRef.current) {
      signatureRef.current = signature;
      setNotifications(next);
    }
  }, []);

  const poll = useCallback(async () => {
    if (pollInFlightRef.current) return;
    pollInFlightRef.current = true;
    const tenantName = currentTenantName();
    const version = mutationVersionRef.current;
    try {
      const feed = await fetchAdminNotifications(tenantName);
      if (version !== mutationVersionRef.current) return;
      if (tenantName !== tenantRef.current) {
        tenantRef.current = tenantName;
        overridesRef.current.clear();
        seenRequestedRef.current.clear();
      }
      const serverList = feed?.notifications ?? [];
      overridesRef.current.forEach((override, id) => {
        const serverItem = serverList.find((notification) => notification.id === id);
        const settled = override === REMOVED ? !serverItem : serverItem?.status === override;
        if (settled) overridesRef.current.delete(id);
      });
      serverListRef.current = serverList;
      publish();
    } catch {
      return;
    } finally {
      pollInFlightRef.current = false;
      setLoaded(true);
    }
  }, [publish]);

  useEffect(() => {
    poll();
    const timer = window.setInterval(() => {
      if (!document.hidden) poll();
    }, POLL_INTERVAL_MS);
    const handleVisibility = () => {
      if (!document.hidden) poll();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [poll]);

  useEffect(() => {
    if (!open) {
      setSessionNewIds((current) => (current.size === 0 ? current : new Set()));
      return;
    }
    const unseen = notifications.filter((notification) => notification.status === "UNSEEN");
    if (unseen.length === 0) return;

    setSessionNewIds((current) => {
      const missing = unseen.filter((notification) => !current.has(notification.id));
      if (missing.length === 0) return current;
      const next = new Set(current);
      missing.forEach((notification) => next.add(notification.id));
      return next;
    });

    const toMark = unseen
      .map((notification) => notification.id)
      .filter((id) => !seenRequestedRef.current.has(id));
    if (toMark.length === 0) return;
    toMark.forEach((id) => seenRequestedRef.current.add(id));
    markAdminNotificationsSeen(currentTenantName(), toMark).catch(() => {
      toMark.forEach((id) => seenRequestedRef.current.delete(id));
    });
  }, [open, notifications]);

  const mutate = useCallback(
    async (ids: string[], override: Override, request: () => Promise<unknown>, failureTitle: string) => {
      if (ids.length === 0) return;
      mutationVersionRef.current += 1;
      ids.forEach((id) => overridesRef.current.set(id, override));
      publish();
      try {
        await request();
      } catch {
        ids.forEach((id) => overridesRef.current.delete(id));
        publish();
        toast({ title: failureTitle, variant: "destructive" });
      } finally {
        mutationVersionRef.current += 1;
        poll();
      }
    },
    [poll, publish, toast],
  );

  const setStatus = useCallback(
    (id: string, status: AdminSettableStatus) => {
      const override: Override = status === "DELETED" ? REMOVED : status;
      return mutate(
        [id],
        override,
        () => updateAdminNotificationStatus(id, currentTenantName(), status),
        "Could not update the notification",
      );
    },
    [mutate],
  );

  const clearAll = useCallback(() => {
    const ids = notifications
      .filter((notification) => notification.status !== "LOCKED")
      .map((notification) => notification.id);
    return mutate(
      ids,
      REMOVED,
      () => clearAdminNotifications(currentTenantName()),
      "Could not clear notifications",
    );
  }, [mutate, notifications]);

  const markAllRead = useCallback(() => {
    const ids = notifications
      .filter((notification) => notification.status === "UNSEEN" || notification.status === "SEEN")
      .map((notification) => notification.id);
    return mutate(
      ids,
      "READ",
      () => markAllAdminNotificationsRead(currentTenantName()),
      "Could not mark notifications as read",
    );
  }, [mutate, notifications]);

  const unseenCount = useMemo(
    () => notifications.filter((notification) => notification.status === "UNSEEN").length,
    [notifications],
  );

  const unreadCount = useMemo(
    () => notifications.filter((notification) => notification.status === "UNSEEN" || notification.status === "SEEN").length,
    [notifications],
  );

  const requestUnreadCount = useMemo(
    () => notifications.filter((notification) => notification.category === "RESOURCE_REQUEST"
      && (notification.status === "UNSEEN" || notification.status === "SEEN")).length,
    [notifications],
  );

  const hasCriticalUnread = useMemo(
    () => notifications.some((notification) => notification.severity?.toUpperCase() === "CRITICAL"
      && (notification.status === "UNSEEN" || notification.status === "SEEN")),
    [notifications],
  );

  const clearableCount = useMemo(
    () => notifications.filter((notification) => notification.status !== "LOCKED").length,
    [notifications],
  );

  return {
    notifications,
    loaded,
    unseenCount,
    unreadCount,
    requestUnreadCount,
    hasCriticalUnread,
    clearableCount,
    sessionNewIds,
    markRead: (id: string) => setStatus(id, "READ"),
    markUnread: (id: string) => setStatus(id, "UNSEEN"),
    lock: (id: string) => setStatus(id, "LOCKED"),
    unlock: (id: string) => setStatus(id, "READ"),
    remove: (id: string) => setStatus(id, "DELETED"),
    markAllRead,
    clearAll,
  };
}
